import {
  Chart,
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Tooltip,
} from "chart.js";

Chart.register(
  LineController,
  Tooltip,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
);
let chart; // define here because chartjs doesn't work if wrapped in a Proxy, read here: https://sebastiandedeyne.com/non-reactive-data-in-alpine-js/

export default (apiUrl, locale, chartType, defaultRange) => ({
  chart: undefined,
  millisecondsPerDay: 86400000,
  today: new Date(),
  selectedRange: defaultRange,
  selected: "",
  isLoading: false,
  priceYesterday: false,
  percentageChange: false,

  init() {
    const ctx = this.$refs.canvas;
    const config = createChartConfig({ type: chartType, locale: locale });

    chart = new Chart(ctx, config);

    // Get initial data and update chart
    this.getDataFromApiAndUpdateChart();

    // Update chart if 'selectedRange' changes
    this.$watch("selectedRange", () => this.onRangeChanged());
  },

  onRangeChanged() {
    this.getDataFromApiAndUpdateChart();
  },

  getDataFromApiAndUpdateChart() {
    this.getDataFromApi().then((data) => {
      this.updatePercentageChange(data);
      this.updateChartData(data);
    });
  },

  async getDataFromApi() {
    this.isLoading = true;
    const requestBody = this.buildRequestBody();
    let chartData;

    await fetch(apiUrl, {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => response.json())
      .then((result) => {
        const data = result.payload.data;
        const prices = data.map((entry) => {
          return entry.buyPrice;
        });
        const labels = data.map((entry) => {
          const date = new Date(entry.date.replace(/ /g, "T"));
          return date.toLocaleString(
            locale,
            dateFormat[requestBody.resolution],
          );
        });

        const priceYesterday = new Intl.NumberFormat(locale, {
          style: "currency",
          currency: "EUR",
        }).format(data.pop().buyPrice);

        chartData = { prices, labels, priceYesterday };
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.isLoading = false;
      });

    return chartData;
  },

  updateChartData({ prices, labels, priceYesterday }) {
    this.priceYesterday = priceYesterday;

    chart.data.labels = labels;

    chart.data.datasets.forEach((dataset) => {
      dataset.data = [...prices];
    });

    chart.update("show");
  },

  updatePercentageChange({ prices }) {
    this.percentageChange = (
      ((prices[prices.length - 1] - prices[0]) / prices[0]) *
      100
    ).toFixed(2);
  },

  buildRequestBody() {
    let toDate = this.today;
    toDate.setMinutes(toDate.getMinutes() + 30);
    toDate.setMinutes(0);
    toDate = new Date(toDate - this.millisecondsPerDay);

    let fromDate;
    let resolutionForRequest;
    switch (this.selectedRange) {
      case "7D":
        fromDate = new Date(toDate - this.millisecondsPerDay * 6);
        resolutionForRequest = "DAYS";
        break;
      case "30D":
        fromDate = new Date(toDate - this.millisecondsPerDay * 30);
        resolutionForRequest = "DAYS";
        break;
      case "MAX":
        fromDate = new Date("2022-09-27T00:00:00");
        resolutionForRequest = "DAYS";
        break;
    }

    return {
      from: fromDate,
      to: toDate,
      resolution: resolutionForRequest,
    };
  },
});

function createChartConfig(options) {
  const { type, locale } = options;

  return {
    type: "line",
    data: {
      labels: [],
      datasets: [
        {
          borderWidth: 4,
          radius: 0,
          tension: 0.1,
          data: [],
          borderColor: "#db0000",
        },
      ],
    },
    options: {
      maintainAspectRatio: false,
      interaction: {
        mode: "nearest",
      },
      elements: {
        point: {
          radius: 10,
          hitRadius: 30,
          backgroundColor: "transparent",
        },
      },
      scales: {
        y: {
          grid: {
            color: "#979797",
            borderDash: [2, 20],
            lineWidth: 2,
            display: true,
            borderWidth: 0,
            drawTicks: true,
          },
          ticks: {
            display: true,
            maxTicksLimit: 10,
            callback: function (val) {
              const label = new Intl.NumberFormat(locale, {
                style: "currency",
                currency: "EUR",
                maximumFractionDigits: 0,
              }).format(val);
              return label;
            },
          },
        },
        x: {
          ticks: {
            autoSkip: true,
            maxRotation: 45,
            minRotation: 45,
          },
        },
      },
      plugins: {
        // format tooltip labels
        tooltip: {
          displayColors: false,
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || "";
              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += new Intl.NumberFormat(locale, {
                  style: "currency",
                  currency: "EUR",
                }).format(context.parsed.y);
              }
              return label;
            },
          },
        },
      },
    },
  };
}

const dateFormat = {
  DAYS: {
    month: "short",
    day: "numeric",
  },
  WEEKS: {
    month: "short",
    day: "numeric",
  },
  MONTHS: {
    day: undefined,
    year: undefined,
    month: "long",
  },
};
